// Bithumb Broker custom settings panel.
//
// Single-row credentials — Bithumb has no paper mode, so the paper/live
// split from KIS doesn't apply. Still reuses the "dirty-draft / change →
// save" UX shape from KISBrokerPanel so the two panels feel consistent.
//
// Test connection issues GET /v1/accounts against the stored keys and
// returns a small teaser of balances (KRW + top 3 non-zero coins) so
// the operator can verify they're pointing at the right account without
// leaving the Settings page.

import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { Group } from '@/admin-design/_common/settings/types'
import { Badge, Button, Card, Input } from '@/admin-design/_common'
import { useCustomPanelForm } from '@/admin-design/_common/settings/useCustomPanelForm'

interface Props {
  group: Group
  endpoint: string
  onDirtyChange?: (dirty: boolean) => void
}

interface SettingsKey {
  value: string
  encrypted: boolean
  masked: boolean
}

interface BithumbPayload {
  access_key: SettingsKey
  secret_key: SettingsKey
  configured: boolean
  endpoint: string
}

interface BithumbTestResult {
  ok: boolean
  latency_ms?: number
  error?: string
  balances?: Array<{ currency: string; balance: string }>
}

const SECRET_SENTINEL = '__unchanged__'

export default function BithumbBrokerPanel({ group, endpoint, onDirtyChange }: Props) {
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<BithumbTestResult | null>(null)

  const form = useCustomPanelForm<BithumbPayload, { accessKey: string; secretKey: string }>({
    endpoint,
    toDraft: () => ({
      accessKey: SECRET_SENTINEL,
      secretKey: SECRET_SENTINEL,
    }),
    isDirty: (_server, draft) =>
      draft.accessKey !== SECRET_SENTINEL || draft.secretKey !== SECRET_SENTINEL,
    toBody: (draft) => {
      const body: Record<string, string> = {}
      if (draft.accessKey !== SECRET_SENTINEL) body.access_key = draft.accessKey
      if (draft.secretKey !== SECRET_SENTINEL) body.secret_key = draft.secretKey
      return body
    },
    onDirtyChange,
  })

  const { draft, setDraft, dirty, reset } = form
  const accessKey = draft.accessKey
  const secretKey = draft.secretKey
  const setAccessKey = (v: string) => setDraft((d) => ({ ...d, accessKey: v }))
  const setSecretKey = (v: string) => setDraft((d) => ({ ...d, secretKey: v }))

  const handleSave = async () => {
    try {
      await form.save()
      setToast('저장되었습니다')
      setError(null)
      setTestResult(null)
      setTimeout(() => setToast(null), 1500)
    } catch (_e) {
      // form.error will be set by the hook
    }
  }

  const test = useMutation({
    mutationFn: async (): Promise<BithumbTestResult> => {
      const r = await fetch('/api/admin/bithumb-credentials/test', {
        method: 'POST',
        credentials: 'include',
      })
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    onSuccess: (res) => {
      setTestResult(res)
      if (res.ok) {
        setToast(`✓ 연결 성공 (${res.latency_ms ?? '?'}ms)`)
        setError(null)
      } else {
        setError(`✗ ${res.error ?? 'unknown'}`)
      }
      setTimeout(() => setToast(null), 2500)
    },
    onError: (e) => setError(String(e)),
  })

  if (form.isLoading) {
    return (
      <Card>
        <Card.Body className="text-sm text-muted-foreground">
          Loading {group.title}…
        </Card.Body>
      </Card>
    )
  }
  if (!form.server) {
    return (
      <Card className="border-destructive-soft bg-destructive-soft">
        <Card.Body className="text-sm text-destructive-soft-foreground">
          Failed to load {group.title}
        </Card.Body>
      </Card>
    )
  }

  const payload = form.server

  return (
    <Card>
      <Card.Header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Card.Title className="flex items-center gap-2 flex-wrap">
            <span>{group.title}</span>
            {payload.configured ? (
              <Badge variant="success">configured</Badge>
            ) : (
              <Badge variant="warning">not configured</Badge>
            )}
            {dirty && (
              <span className="text-xs font-normal text-warning">● unsaved</span>
            )}
          </Card.Title>
          {group.description && (
            <Card.Description>{group.description}</Card.Description>
          )}
          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
            {payload.endpoint}
          </p>
        </div>
      </Card.Header>

      {(error || form.error) && (
        <div className="border-b border-destructive-soft bg-destructive-soft px-6 py-3 text-sm text-destructive-soft-foreground">
          {error || (form.error ? String(form.error.message) : '')}
        </div>
      )}

      <div className="divide-y divide-border">
        <SecretRow
          label="Access Key"
          masked={payload.access_key?.value ?? ''}
          draft={accessKey}
          onChange={setAccessKey}
        />
        <SecretRow
          label="Secret Key"
          masked={payload.secret_key?.value ?? ''}
          draft={secretKey}
          onChange={setSecretKey}
        />
      </div>

      {testResult?.balances && testResult.balances.length > 0 && (
        <div className="border-t border-border bg-muted/30 px-6 py-3 text-xs">
          <p className="mb-1 font-medium text-muted-foreground">
            연결된 계정 요약 (KRW + 상위 3 자산)
          </p>
          <ul className="space-y-0.5 font-mono">
            {testResult.balances.map((b) => (
              <li key={b.currency}>
                <span className="mr-2 text-muted-foreground">{b.currency}</span>
                <span className="text-foreground">{b.balance}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Card.Footer className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          {toast
            ? toast
            : dirty
            ? '변경사항이 저장되지 않았습니다'
            : '변경사항 없음'}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!payload.configured || test.isPending || dirty}
            loading={test.isPending}
            onClick={() => test.mutate()}
            title={
              dirty
                ? '변경사항을 먼저 저장한 뒤 테스트하세요'
                : payload.configured
                ? '저장된 자격증명으로 Bithumb /v1/accounts 호출을 시도합니다'
                : '자격증명 저장 후 테스트 가능'
            }
          >
            Test connection
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!dirty || form.isSaving}
            onClick={reset}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || form.isSaving}
            loading={form.isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </Card.Footer>
    </Card>
  )
}

function SecretRow({
  label,
  masked,
  draft,
  onChange,
}: {
  label: string
  masked: string
  draft: string
  onChange: (v: string) => void
}) {
  const editing = draft !== SECRET_SENTINEL
  return (
    <div className="px-6 py-4">
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-[minmax(160px,240px)_1fr]">
        <label className="text-sm font-medium text-foreground md:pt-2">
          {label}
        </label>
        <div>
          {!editing ? (
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-foreground">
                {masked || <span className="text-muted-foreground">(not set)</span>}
              </span>
              <Button variant="secondary" size="sm" onClick={() => onChange('')}>
                Change
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                type="text"
                className="font-mono"
                value={draft}
                onChange={(e) => onChange(e.target.value)}
                autoFocus
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onChange(SECRET_SENTINEL)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
